// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Dictionary } from "@insite/client-framework/Common/Types";
import translate from "@insite/client-framework/Translate";
import { TypographyProps } from "@insite/mobius/Typography";
import breakpoints from "@vanmeter/XDL/PreStyle/breakpoints";

/**
 *
 * we have used this equation vw=(fontSize/viewportWidth) × 100 to adjust font size on tablet screen
 * @param mobile
 * @param tablet
 * @param desktop
 * @returns
 */
export const autoClamp = (mobile: number, tablet: number, desktop: number) => {
    const vw = ((tablet / breakpoints.values[2]) * 100).toFixed(2);
    return `clamp(${mobile}px ,${vw}vw , ${desktop}px)`;
};

export const CART_ORDER_SUMMARY_ELEMENT_ID = "cart-order-summary";

export const constants = {
    footer: {
        offsetTop: 64,
    },
    searchInput: {
        breakpoints: {
            lg: 1280,
        },
    },
    cartLine: {
        breakpoints: {
            md: 1200,
            xs: "xs",
        },
    },
    productList: {
        xSortAndFilter: {
            screen: "isTabletScreen",
            breakpoint: "md",
        },
        breakpoints: {
            md: 1220,
        },
    },
    productGridView: {
        desktop: {
            width: 317,
            height: 308,
        },
        tablet: {
            width: 328,
            height: 230,
        },
        mobile: {
            width: 328,
            height: 280,
        },
    },
    Dashboard: {
        breakpoints: { md: 1140 },
    },
    myAccount: { breakpoints: { xs: 400 } },
};

export const EMPTY_VALUE = "- -";
export const DivWrapper = `div[class^="Wrapper"]`;

export const PLP_BREAKPOINT = 1200;
export const CART_BREAKPOINT = 1180;

export const CUSTOM_ELEMENTS = "Custom" as any;

export const VMI_EVENT_ELEMENTS = "Vmi Event" as any;

export const NOTIFY_TYPE_NO_RESULTS = "warning";

export const MY_ACCOUNT_LIST_ELEMENTS = [
    "OrderHistoryPage",
    "VmiOrderHistoryPage",
    "OrderHistoryRealTimePage",
    "SavedOrderListPage",
    "OrderApprovalListPage",
    "InvoiceHistoryPage",
    "MyListsPage",
    "RfqMyQuotesPage",
    "VmiReportingPage",
    "VmiOrderHistoryPage",
    "VmiBinsPage",
    "VmiBinDetailsPage",
    "VMIAccountSummaryPage",
] as any;

export const contentBucketVariants = {
    infoCard: "info-card",
    infoCardText: "info-card-text",
    graphicCallOut: "graphic-call-out",
    callOutLarge: "call-out-large",
    callOutMini: "call-out-mini",
    postCard: "post-card",
    authorCard: "author-card",
};

export const sharedDateTimeOptions: Intl.DateTimeFormatOptions = {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
};

export const placeHolders = {
    email: "Email@Address.com",
    phone: "(__) __ – ____",
};

export const USA_ABBR = "US";

export const addToCartStatus = {
    default: {
        status: "default",
        label: translate("Add to Cart"),
        icon: "",
    },
    loading: {
        status: "loading",
        label: translate("Adding"),
        icon: "",
    },
    success: {
        status: "success",
        label: translate("Added"),
        icon: "",
    },
};

export const fontConfig = {
    url: "/staticContent/stylesheet.css",
    fontFamily: "Adelle, Arial, sans-serif",
    secondaryFontFamily: "TVNord, Arial, sans-serif",
    fontName: ["Adelle", "TVNord"],
    weight: {
        regular: 400,
        bold: 700,
    },
    xxSmall: {
        size: 10,
        lineHeight: "14px",
    },
    xxSmallSingleLine: {
        size: 10,
        lineHeight: "13px",
    },
    xSmall: {
        size: 12,
        lineHeight: "20px",
    },
    xSmallSingleLine: {
        size: 12,
        lineHeight: "16px",
    },
    small: {
        size: 14,
        lineHeight: "22px",
    },
    smallSingleLine: {
        size: 14,
        lineHeight: "18px",
    },
    regular: {
        size: 16,
        lineHeight: "25px",
    },
    regularSingleLine: {
        size: 16,
        lineHeight: "21px",
    },
    h1Special: {
        size: autoClamp(52, 52, 64),
        lineHeight: autoClamp(52, 52, 64),
    },
    h2Special: {
        size: autoClamp(40, 40, 48),
        lineHeight: autoClamp(40, 40, 48),
    },
};

export const paragraphStyles = {
    "as-h1": "As H1",
    "as-h2": "As H2",
    "as-h3": "As H3",
    "as-h4": "As H4",
    "as-h5": "As H5",
    "as-h6": "As H6",
    "special-h1": "Special H1",
    "special-h2": "Special H2",
    "as-blue": "Text Blue",
    "as-yellow": "Text Yellow",
    "as-orange": "Text Orange",
    "as-white": "Text White",
};

export const classToVariant: Dictionary<TypographyProps["variant"]> = {
    "as-h1": "h1",
    "as-h2": "h2",
    "as-h3": "h3",
    "as-h4": "h4",
    "as-h5": "h5",
    "as-h6": "h6",
};

export const customRichContentConfig = {
    extendedConfig: {
        paragraphStyles,
        paragraphMultipleStyles: true,
        linkMultipleStyles: true,
        tableStyles: {
            // Default from library
            "fr-dashed-borders": "Dashed Borders",
            "fr-alternate-rows": "Alternate Rows",
            // Custom
            "vmi-table-style": "VMI Table Style",
        },
        paragraphFormat: {
            N: "Normal",
            span: "span",
            H1: "Heading 1",
            H2: "Heading 2",
            H3: "Heading 3",
            H4: "Heading 4",
            H5: "Heading 5",
            H6: "Heading 6",
            PRE: "Code",
        },
        linkEditButtons: ["linkOpen", "linkStyle", "linkEdit", "linkRemove"],
        linkStyles: {
            primary: "Primary",
            secondary: "Secondary",
            tertiary: "Tertiary",
            quarternary: "Quarternary",
        },
    },
    expandedToolbarButtons: {
        moreTable: {
            buttons: ["insertTable"],
            buttonsVisible: 1,
        },
        accordion: {
            buttons: ["accordion"],
            buttonsVisible: 1,
        },
    },
    collapsedToolbarButtons: {
        moreText: { buttons: ["paragraphFormat", "paragraphStyle", "textColor", "bold", "italic"] },
    },
};

export const BUTTON_CMS_VARIANT_OPTIONS = [
    { displayName: "Primary", value: "primary" },
    { displayName: "Secondary", value: "secondary" },
    { displayName: "Tertiary", value: "tertiary" },
    { displayName: "Primary - outline", value: "primary-outline" },
    { displayName: "Secondary - outline", value: "secondary-outline" },
    { displayName: "Tertiary - outline", value: "tertiary-outline" },
    { displayName: "Link", value: "link" },
];

export const LINK_CMS_VARIANT_OPTIONS = [
    { displayName: "Primary", value: "primary" },
    { displayName: "Secondary", value: "secondary" },
    { displayName: "Tertiary", value: "tertiary" },
    { displayName: "Quarternary", value: "quarternary" },
];

export const ErrorApiDefaultMessage = "Please try again and contact support if you continue to have issues.";

// XNG-Changes:rma return reason
export const DAMAGED = "Defective/Damaged";

export enum PlpContentTabs {
    products = "products",
    content = "content",
}

export enum ProductRelationshipTypes {
    crosssell = "crosssell",
    substitutes = "substitutes",
}

export const OrderPdfAttachmentUrl = "api/vmi/orderAttachment/getpdf";
export const QuotePdfAttachmentUrl = "api/vmi/quoteAttachment/getPdf";

export enum OrderStatusQueryString {
    closedOrders = "closedOrders",
    openOrders = "openOrders",
}
